import { inject, Injectable } from '@angular/core';
import { environment } from '@envs/environment';
import { SessionStorageService } from '../storage/session.storage.service';
import { NotificacionService } from '../notificacion.service';
import { LoadingService } from '../loading.service';
import { TypeAlertEnum } from '@shared/enums/type.alert.enum';
import { firstValueFrom, Subject, take, timeout } from 'rxjs';

/**
 * Gestiona la autenticación y la navegación hacia SIAFP Web (legado).
 * PLUS-0092-H1-H2: login paralelo en iframe, clic Web en la misma pestaña y control de idle.
 */
@Injectable({
    providedIn: 'root'
})
export class PortalWebAuthService {

    private readonly _storage = inject(SessionStorageService);
    private readonly _notificacion = inject(NotificacionService);
    private readonly _loading = inject(LoadingService);

    private static readonly SESSION_TS_KEY = 'siafp-web-session-ts';
    private static readonly TOP_LEVEL_KEY = 'siafp-web-toplevel';
    private static readonly IFRAME_ID = 'siafp-web-auth-frame';
    private static readonly WEB_LOGOUT_PATH = '/WEB-PortalAsofondos/salir.do';
    private static readonly WEB_LOGOUT_IFRAME_ID = 'siafp-web-logout-frame';
    private static readonly WEB_LOGOUT_IFRAME_TIMEOUT_MS = 5000;
    private static readonly MSG_ERROR = 'Hubo un problema de autenticación con SIAFP. Intente de nuevo.';

    private readonly _authOk$ = new Subject<void>();
    private _escuchando = false;

    private get idleMs(): number {
        return (environment.SIAFP_WEB_IDLE_MINUTES ?? 10) * 60 * 1000;
    }

    private get authTimeoutMs(): number {
        return environment.SIAFP_WEB_AUTH_TIMEOUT_MS ?? 4000;
    }

    private get toastMs(): number {
        return environment.SIAFP_WEB_TOAST_MS ?? 4000;
    }

    constructor() {
        this.escucharConfirmacionAuth();
    }

    /** Construye la URL del Action de autenticación Web. */
    urlAutenticacion(): string {
        return `${environment.URL_SIAFP}${environment.SIAFP_WEB_AUTH_PATH}`;
    }

    /**
     * Convierte una opción de menú en URL absoluta contra URL_SIAFP.
     *
     * @param urlOpcion path o URL de la opción
     * @returns URL absoluta, o vacío si la opción no es válida
     */
    urlAbsoluta(urlOpcion: string): string {
        const ruta = (urlOpcion ?? '').trim();
        if (!ruta || ruta === '-') {
            return '';
        }
        if (/^https?:\/\//i.test(ruta)) {
            return ruta;
        }
        return `${environment.URL_SIAFP}${ruta.startsWith('/') ? ruta : '/' + ruta}`;
    }

    /**
     * Obtiene el path relativo de una opción (para redirectUrl).
     *
     * @param urlOpcion path o URL de la opción
     * @returns path relativo, o vacío si la opción no es válida
     */
    pathRelativo(urlOpcion: string): string {
        const ruta = (urlOpcion ?? '').trim();
        if (!ruta || ruta === '-') {
            return '';
        }
        if (/^https?:\/\//i.test(ruta)) {
            try {
                const u = new URL(ruta);
                return `${u.pathname}${u.search}`;
            } catch {
                return '';
            }
        }
        return ruta.startsWith('/') ? ruta : `/${ruta}`;
    }

    /** Indica si el idle de sesión Web sigue vigente. */
    sesionWebViva(): boolean {
        const ts = this._storage.getItem<number>(PortalWebAuthService.SESSION_TS_KEY);
        if (ts == null || typeof ts !== 'number') {
            return false;
        }
        return (Date.now() - ts) < this.idleMs;
    }

    /** Marca el instante de confirmación de sesión Web. */
    marcarSesionWebActiva(): void {
        this._storage.setItem(PortalWebAuthService.SESSION_TS_KEY, Date.now());
    }

    /** Elimina las marcas de idle y de cookie top-level de SIAFP Web. */
    limpiarSesionWeb(): void {
        this._storage.removeItem(PortalWebAuthService.SESSION_TS_KEY);
        this._storage.removeItem(PortalWebAuthService.TOP_LEVEL_KEY);
    }

    /**
     * Solicita el cierre de la sesion del portal Web y limpia sus marcas locales.
     * El portal Web conserva sus reglas para multiples ventanas.
     */
    cerrarSesionWeb(): void {
        if (!this.sesionWebViva()) {
            this.limpiarSesionWeb();
            return;
        }

        const urlLogout = this.urlAbsoluta(PortalWebAuthService.WEB_LOGOUT_PATH);
        if (!urlLogout) {
            console.error('[PortalWebAuthService.cerrarSesionWeb] URL de cierre no disponible.');
            this.limpiarSesionWeb();
            return;
        }

        const iframeExistente = document.getElementById(
            PortalWebAuthService.WEB_LOGOUT_IFRAME_ID
        );
        if (iframeExistente != null) {
            iframeExistente.remove();
        }

        const iframe = document.createElement('iframe');
        iframe.id = PortalWebAuthService.WEB_LOGOUT_IFRAME_ID;
        iframe.title = 'siafp-web-logout';
        iframe.setAttribute('aria-hidden', 'true');
        iframe.tabIndex = -1;
        iframe.style.cssText =
            'position:absolute;width:0;height:0;border:0;left:-9999px;top:-9999px;visibility:hidden;';
        iframe.src = urlLogout;
        document.body.appendChild(iframe);

        window.setTimeout(() => {
            iframe.remove();
        }, PortalWebAuthService.WEB_LOGOUT_IFRAME_TIMEOUT_MS);

        this.limpiarSesionWeb();
    }

    /**
     * Autentica SIAFP Web en paralelo al login de PLUS mediante iframe oculto.
     * No marca idle hasta que Web confirma con postMessage.
     *
     * @param username usuario PLUS
     * @param password clave del portal Web
     */
    iniciarSesionSilenciosa(username: string, password: string): void {
        if (!username || !password) {
            return;
        }
        this.enviarAutenticacion({
            username,
            password,
            target: 'iframe'
        });
    }

    /**
     * Abre una opción Web en la misma pestaña.
     * Si la autenticación falla, permanece en PLUS y muestra un toast.
     * PLUS-0092-H4: envía returnUrl, nombrePlus y usuarioPlus para el header Web.
     *
     * @param urlOpcion path o URL de la opción
     * @param username usuario PLUS
     * @param password clave del portal Web
     * @param nombrePlus nombre visible de PLUS
     * @param usuarioPlus login visible de PLUS
     */
    async abrirOpcion(
        urlOpcion: string,
        username: string,
        password: string,
        nombrePlus?: string,
        usuarioPlus?: string
    ): Promise<void> {
        const absoluta = this.urlAbsoluta(urlOpcion);
        const relativa = this.pathRelativo(urlOpcion);
        if (!absoluta || !relativa) {
            this.notificarErrorAuth('abrirOpcion');
            return;
        }

        if (!username || !password) {
            this.notificarErrorAuth('abrirOpcion');
            return;
        }

        if (!(this.sesionWebViva() && this.tieneSesionTopLevel())) {
            const ok = await this.confirmarAutenticacion(username, password);
            if (!ok) {
                this.limpiarSesionWeb();
                this.notificarErrorAuth('abrirOpcion');
                return;
            }
        }

        this.enviarAutenticacion({
            username,
            password,
            redirectUrl: relativa,
            returnUrl: window.location.href,
            nombrePlus,
            usuarioPlus,
            target: '_self'
        });
        this.marcarSesionTopLevel();
        this.marcarSesionWebActiva();
    }

    /**
     * Abre el portal Web desde el header en una pestaña nueva, sin redirectUrl.
     *
     * @param username usuario PLUS
     * @param password clave del portal Web
     * @param nombrePlus nombre visible de PLUS
     * @param usuarioPlus login visible de PLUS
     */
    async abrirPortalDesdeHeader(
        username: string,
        password: string,
        nombrePlus?: string,
        usuarioPlus?: string
    ): Promise<void> {
        if (!username || !password) {
            this.notificarErrorAuth('abrirPortalDesdeHeader');
            return;
        }

        if (!this.sesionWebViva()) {
            const ok = await this.confirmarAutenticacion(username, password);
            if (!ok) {
                this.limpiarSesionWeb();
                this.notificarErrorAuth('abrirPortalDesdeHeader');
                return;
            }
        }

        this.enviarAutenticacion({
            username,
            password,
            returnUrl: window.location.href,
            nombrePlus,
            usuarioPlus,
            target: '_blank'
        });
        this.marcarSesionTopLevel();
        this.marcarSesionWebActiva();
    }

    private tieneSesionTopLevel(): boolean {
        return this._storage.getItem<boolean>(PortalWebAuthService.TOP_LEVEL_KEY) === true;
    }

    private marcarSesionTopLevel(): void {
        this._storage.setItem(PortalWebAuthService.TOP_LEVEL_KEY, true);
    }

    private notificarErrorAuth(metodo: string): void {
        console.error(`[PortalWebAuthService.${metodo}] Fallo de autenticacion con SIAFP Web.`);
        this._notificacion.mostrarNotificacionBasica(
            PortalWebAuthService.MSG_ERROR,
            TypeAlertEnum.ERROR,
            this.toastMs
        );
    }

    private origenWeb(): string {
        try {
            return new URL(environment.URL_SIAFP).origin;
        } catch {
            return '';
        }
    }

    private escucharConfirmacionAuth(): void {
        if (this._escuchando) {
            return;
        }
        this._escuchando = true;
        window.addEventListener('message', (event: MessageEvent) => {
            if (event.origin !== this.origenWeb()) {
                return;
            }
            const data = event.data as { source?: string; event?: string } | null;
            if (data?.source !== 'siafp-web' || data?.event !== 'auth-ok') {
                return;
            }
            this.marcarSesionWebActiva();
            this._authOk$.next();
        });
    }

    private async confirmarAutenticacion(username: string, password: string): Promise<boolean> {
        this._loading.show();
        try {
            const confirmada = firstValueFrom(
                this._authOk$.pipe(
                    take(1),
                    timeout({ first: this.authTimeoutMs })
                )
            );
            this.enviarAutenticacion({
                username,
                password,
                target: 'iframe'
            });
            await confirmada;
            return true;
        } catch {
            return false;
        } finally {
            this._loading.hide();
        }
    }

    /**
     * Envia el POST de autenticacion Web.
     * PLUS-0092-H4: returnUrl, nombrePlus y usuarioPlus viajan al header Web.
     *
     * @param opts credenciales, destino y datos de header opcionales
     */
    private enviarAutenticacion(opts: {
        username: string;
        password: string;
        redirectUrl?: string;
        returnUrl?: string;
        nombrePlus?: string;
        usuarioPlus?: string;
        target: '_self' | '_blank' | 'iframe';
    }): void {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = this.urlAutenticacion();
        form.style.display = 'none';

        if (opts.target === 'iframe') {
            let iframe = document.getElementById(PortalWebAuthService.IFRAME_ID) as HTMLIFrameElement | null;
            if (!iframe) {
                iframe = document.createElement('iframe');
                iframe.id = PortalWebAuthService.IFRAME_ID;
                iframe.name = PortalWebAuthService.IFRAME_ID;
                iframe.title = 'siafp-web-auth';
                iframe.setAttribute('aria-hidden', 'true');
                iframe.tabIndex = -1;
                iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;left:-9999px;top:-9999px;visibility:hidden;';
                document.body.appendChild(iframe);
            }
            form.target = PortalWebAuthService.IFRAME_ID;
        } else {
            form.target = opts.target;
        }

        const datos: { [key: string]: string } = {
            username: opts.username.toUpperCase(),
            password: opts.password
        };
        if (opts.redirectUrl) {
            datos['redirectUrl'] = opts.redirectUrl;
        }
        if (opts.returnUrl) {
            datos['returnUrl'] = opts.returnUrl;
        }
        if (opts.nombrePlus) {
            datos['nombrePlus'] = opts.nombrePlus;
        }
        if (opts.usuarioPlus) {
            datos['usuarioPlus'] = opts.usuarioPlus;
        }

        for (const key of Object.keys(datos)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = datos[key];
            form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }
}
